import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { ShieldCheck, Camera, Video, CheckCircle2, Loader2, RotateCcw, IdCard, BookOpen, Lock, AlertTriangle } from "lucide-react";

const pub = axios.create({ withCredentials: false });
const MIN_SEC = 5;
const MAX_SEC = 10;

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  return ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
}

function Shell({ children, firm }) {
  return (
    <div className="min-h-screen bg-[var(--bg)] flex justify-center px-4 py-8 sm:py-14" data-testid="voi-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={18} className="text-[var(--moss)]" />
          <span className="overline">{firm || "Secure identity check"}</span>
        </div>
        {children}
        <p className="mt-8 text-[11px] text-[var(--ink-3)] flex items-start gap-1.5">
          <Lock size={11} className="mt-0.5 shrink-0" />
          Your photo and video are encrypted, seen only by your accountant, and permanently destroyed straight after review.
          Never type your TFN anywhere on this page.
        </p>
      </div>
    </div>
  );
}

function Steps({ step }) {
  return (
    <div className="flex gap-1.5 mb-6" data-testid="voi-steps">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-500"
             style={{ background: i <= step ? "var(--ink)" : "var(--subtle)" }} />
      ))}
    </div>
  );
}

function ClosedState({ status }) {
  const map = {
    WAITING_FOR_REVIEW: ["Thanks — you're all done", "Your accountant will review your ID shortly. You can close this page."],
    APPROVED: ["Identity verified", "This check is complete. You can close this page."],
    REJECTED: ["This check is closed", "Your accountant will be in touch about next steps."],
    EXPIRED: ["This link has expired", "Ask your accountant to send you a new link."],
  };
  const [t, d] = map[status] || map.EXPIRED;
  const ok = status === "WAITING_FOR_REVIEW" || status === "APPROVED";
  return (
    <div className="card p-6" data-testid="voi-closed-state">
      {ok ? <CheckCircle2 className="text-[var(--moss)] mb-3" size={28} /> : <AlertTriangle className="text-[var(--accent)] mb-3" size={28} />}
      <h1 className="font-heading text-3xl text-[var(--ink)]" data-testid="voi-closed-title">{t}</h1>
      <p className="text-sm text-[var(--ink-2)] mt-2">{d}</p>
    </div>
  );
}

function IdStep({ token, onDone }) {
  const [idType, setIdType] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const choose = (f) => {
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
  };
  const upload = async () => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("asset_type", "ID_PHOTO"); fd.append("id_type", idType); fd.append("file", file);
      await pub.post(`${API_BASE}/voi/public/${token}/upload`, fd);
      onDone();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const types = [["DRIVER_LICENCE", "Driver licence", IdCard], ["PASSPORT", "Passport", BookOpen]];
  return (
    <div className="card p-6 space-y-5" data-testid="voi-id-step">
      <div>
        <div className="overline mb-1">Step 1 of 2</div>
        <h1 className="font-heading text-3xl text-[var(--ink)]">Photo of your ID</h1>
        <p className="text-sm text-[var(--ink-2)] mt-1">Lay it flat in good light. All four corners visible, no glare.</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {types.map(([v, l, Icon]) => (
          <button key={v} type="button" onClick={() => setIdType(v)} data-testid={`voi-idtype-${v.toLowerCase()}`}
            className={`p-3 rounded-md border text-left transition-colors ${idType === v ? "border-[var(--ink)] bg-[var(--subtle)]" : "border-[var(--border)] hover:border-[var(--ink-3)]"}`}>
            <Icon size={16} className="mb-1.5" /><div className="text-[13px] font-medium">{l}</div>
          </button>
        ))}
      </div>
      <label className={`block border border-dashed rounded-md p-4 text-center cursor-pointer ${idType ? "border-[var(--ink-3)]" : "opacity-50 pointer-events-none border-[var(--border)]"}`}>
        {preview ? <img src={preview} alt="ID preview" className="max-h-48 mx-auto rounded" data-testid="voi-id-preview" />
          : <span className="text-[13px] text-[var(--ink-2)] inline-flex items-center gap-2"><Camera size={15} /> Take or choose a photo</span>}
        <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="voi-id-file"
               onChange={(e) => choose(e.target.files?.[0] || null)} />
      </label>
      {err && <div className="text-[12px] text-[var(--accent)]" data-testid="voi-id-error">{err}</div>}
      <button className="btn-primary w-full justify-center" disabled={!idType || !file || busy} onClick={upload} data-testid="voi-id-continue">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null} Continue
      </button>
    </div>
  );
}

function useRecorder() {
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const [live, setLive] = useState(false);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [blob, setBlob] = useState(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null; setLive(false);
  }, []);
  useEffect(() => stopStream, [stopStream]);

  const start = async (videoEl) => {
    const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
    streamRef.current = s;
    videoEl.srcObject = s; videoEl.muted = true; await videoEl.play();
    setLive(true); setBlob(null);
  };
  const record = () => {
    const mime = pickMime();
    const rec = new MediaRecorder(streamRef.current, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 1_000_000 });
    chunks.current = [];
    rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    rec.onstop = () => { setBlob(new Blob(chunks.current, { type: (rec.mimeType || "video/webm").split(";")[0] })); stopStream(); };
    recRef.current = rec; rec.start(250); setRecording(true); setSecs(0);
    const t0 = Date.now();
    const iv = setInterval(() => {
      const s = Math.floor((Date.now() - t0) / 1000);
      setSecs(s);
      if (s >= MAX_SEC) { clearInterval(iv); rec.state !== "inactive" && rec.stop(); setRecording(false); }
    }, 200);
    rec.addEventListener("stop", () => { clearInterval(iv); setRecording(false); });
  };
  const stop = () => recRef.current?.state === "recording" && recRef.current.stop();
  return { live, recording, secs, blob, setBlob, start, record, stop };
}

function VideoStep({ token, phrase, onDone }) {
  const videoRef = useRef(null);
  const r = useRecorder();
  const [fallback, setFallback] = useState(typeof MediaRecorder === "undefined" || !navigator.mediaDevices);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const url = useMemo(() => (r.blob ? URL.createObjectURL(r.blob) : null), [r.blob]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const openCam = async () => {
    setErr(null);
    try { await r.start(videoRef.current); } catch { setFallback(true); setErr("We couldn't open your camera — record with your phone's camera instead."); }
  };
  const checkDuration = (f) => {
    const v = document.createElement("video");
    const u = URL.createObjectURL(f);
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(u);
      const d = v.duration;
      if (Number.isFinite(d) && (d < MIN_SEC - 1 || d > MAX_SEC + 5)) setErr(`Please record ${MIN_SEC}–${MAX_SEC} seconds (yours is ${Math.round(d)}s).`);
      else { setErr(null); r.setBlob(f); }
    };
    v.onerror = () => { URL.revokeObjectURL(u); r.setBlob(f); };
    v.src = u;
  };
  const upload = async () => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      const ext = r.blob.type.includes("mp4") ? "mp4" : r.blob.type.includes("quicktime") ? "mov" : "webm";
      fd.append("asset_type", "LIVENESS_VIDEO"); fd.append("file", r.blob, `liveness.${ext}`);
      await pub.post(`${API_BASE}/voi/public/${token}/upload`, fd);
      onDone();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-6 space-y-5" data-testid="voi-video-step">
      <div>
        <div className="overline mb-1">Step 2 of 2</div>
        <h1 className="font-heading text-3xl text-[var(--ink)]">Short selfie video</h1>
        <p className="text-sm text-[var(--ink-2)] mt-1">Hold the same ID next to your face and say these words out loud ({MIN_SEC}–{MAX_SEC} seconds):</p>
      </div>
      <div className="rounded-md bg-[var(--ink)] text-white text-center py-4 font-heading text-2xl tracking-wide" data-testid="voi-phrase">{phrase}</div>
      <div className="relative rounded-md overflow-hidden bg-black aspect-[3/4]" style={{ display: r.live || url ? "block" : "none" }}>
        <video ref={videoRef} playsInline className="w-full h-full object-cover" style={{ display: url ? "none" : "block", transform: "scaleX(-1)" }} data-testid="voi-live-video" />
        {url && <video src={url} controls playsInline className="w-full h-full object-cover" data-testid="voi-recorded-video" />}
        {r.recording && (
          <div className="absolute top-3 left-3 bg-[var(--accent)] text-white text-[12px] px-2 py-1 rounded-full font-mono" data-testid="voi-rec-timer">
            ● REC {r.secs}s
          </div>
        )}
      </div>
      {fallback && !url && (
        <label className="block border border-dashed border-[var(--ink-3)] rounded-md p-4 text-center cursor-pointer">
          <span className="text-[13px] text-[var(--ink-2)] inline-flex items-center gap-2"><Video size={15} /> Record a video with your camera</span>
          <input type="file" accept="video/*" capture="user" className="hidden" data-testid="voi-video-file"
                 onChange={(e) => e.target.files?.[0] && checkDuration(e.target.files[0])} />
        </label>
      )}
      {err && <div className="text-[12px] text-[var(--accent)]" data-testid="voi-video-error">{err}</div>}
      {!fallback && !r.live && !url && (
        <button className="btn-secondary w-full justify-center" onClick={openCam} data-testid="voi-open-camera"><Camera size={14} /> Open camera</button>
      )}
      {r.live && !r.recording && <button className="btn-primary w-full justify-center" onClick={r.record} data-testid="voi-record-start"><Video size={14} /> Start recording</button>}
      {r.recording && (
        <button className="btn-primary w-full justify-center" disabled={r.secs < MIN_SEC} onClick={r.stop} data-testid="voi-record-stop">
          {r.secs < MIN_SEC ? `Keep going… ${MIN_SEC - r.secs}s` : "Stop recording"}
        </button>
      )}
      {url && (
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => { r.setBlob(null); }} data-testid="voi-record-retake"><RotateCcw size={14} /> Retake</button>
          <button className="btn-primary flex-1 justify-center" onClick={upload} disabled={busy} data-testid="voi-video-continue">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Use this video
          </button>
        </div>
      )}
    </div>
  );
}

function SubmitStep({ token, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await pub.post(`${API_BASE}/voi/public/${token}/submit`); onDone(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-6 space-y-4" data-testid="voi-submit-step">
      <CheckCircle2 className="text-[var(--moss)]" size={26} />
      <h1 className="font-heading text-3xl text-[var(--ink)]">Ready to send</h1>
      <p className="text-sm text-[var(--ink-2)]">Your ID photo and video are uploaded. Send them to your accountant for review. This link can't be used again afterwards.</p>
      {err && <div className="text-[12px] text-[var(--accent)]" data-testid="voi-submit-error">{err}</div>}
      <button className="btn-primary w-full justify-center" onClick={submit} disabled={busy} data-testid="voi-submit">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null} Send for review
      </button>
    </div>
  );
}

export default function VoiCapture() {
  const { token } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await pub.get(`${API_BASE}/voi/public/${token}`);
      setS(data);
      setStep(data.uploaded.includes("ID_PHOTO") ? (data.uploaded.includes("LIVENESS_VIDEO") ? 2 : 1) : 0);
    } catch (e) { setErr(formatApiError(e)); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (err) return <Shell><div className="card p-6" data-testid="voi-invalid"><AlertTriangle className="text-[var(--accent)] mb-3" /><h1 className="font-heading text-3xl">Link not valid</h1><p className="text-sm text-[var(--ink-2)] mt-2">{err}. Ask your accountant for a new link.</p></div></Shell>;
  if (!s) return <Shell><Loader2 className="animate-spin text-[var(--ink-3)]" data-testid="voi-loading" /></Shell>;
  if (s.status !== "PENDING_UPLOAD") return <Shell firm={s.firm_name}><ClosedState status={s.status} /></Shell>;
  return (
    <Shell firm={s.firm_name}>
      <p className="text-[15px] text-[var(--ink)] mb-4" data-testid="voi-greeting">
        Hi{s.first_name ? ` ${s.first_name}` : ""}, {s.firm_name} needs to confirm it's really you. About two minutes.
      </p>
      {s.retry_note && (
        <div className="card p-4 mb-4 text-[13px] border-l-4" style={{ borderLeftColor: "var(--accent)" }} data-testid="voi-retry-note">
          <div className="font-medium text-[var(--ink)] mb-0.5">Your last attempt needs redoing</div>
          <div className="text-[var(--ink-2)]">{s.retry_note}</div>
        </div>
      )}
      <Steps step={step} />
      {step === 0 && <IdStep token={token} onDone={() => setStep(1)} />}
      {step === 1 && <VideoStep token={token} phrase={s.dynamic_phrase} onDone={() => setStep(2)} />}
      {step === 2 && <SubmitStep token={token} onDone={() => setS({ ...s, status: "WAITING_FOR_REVIEW" })} />}
    </Shell>
  );
}
